/**
 * The local UTC offset at an Australian inspection site.
 *
 * WHY THE SITE, NOT THE READER
 * ----------------------------
 * Every inspection time on a CER report is LOCAL time at the site — that is
 * what the portal asks for and what the homeowner would recognise. So when a
 * time has to be derived (see inspection-window.ts) the timezone that matters
 * belongs to the address, not to the Lambda (UTC) or to the reviewer's
 * browser (whatever they happen to be in).
 *
 * Getting this wrong is not subtle: a legacy Dropbox import carries no device
 * offset and its photo timestamps are UTC instants, so a midday Springfield
 * inspection rendered as 02:19 — ten hours out, and obviously wrong to anyone
 * who reads a report.
 *
 * DST
 * ---
 * QLD, WA and NT do not observe it. The rest shift +1h from the first Sunday
 * in October to the first Sunday in April. Transitions happen at 2am local;
 * this resolves at DAY precision, which is right for every use here (an
 * inspection is a daytime visit, and a report whose window straddles the
 * changeover hour is not a case worth modelling).
 */
import { stateFromPostcode } from './au-postcode.js';
/** Standard-time offset in minutes east of UTC, by state. */
const STANDARD_OFFSET_MINUTES = {
    QLD: 600,
    NSW: 600,
    VIC: 600,
    ACT: 600,
    TAS: 600,
    SA: 570,
    NT: 570,
    WA: 480,
};
/** States that observe daylight saving. */
const OBSERVES_DST = new Set(['NSW', 'VIC', 'ACT', 'TAS', 'SA']);
/**
 * The offset every Leading Edge device reports, and the fallback when the
 * site's state cannot be determined. AEST — the business runs out of QLD.
 */
export const DEFAULT_AU_OFFSET_MINUTES = 600;
/** Day-of-month of the first Sunday in `month` (0-indexed) of `year`, UTC. */
function firstSundayOfMonth(year, month) {
    const firstDow = new Date(Date.UTC(year, month, 1)).getUTCDay();
    return firstDow === 0 ? 1 : 8 - firstDow;
}
/**
 * Is this instant inside the southern DST window, judged on the local
 * calendar date at standard time? October → April spans New Year, so the
 * window is "on/after the October Sunday" OR "before the April Sunday".
 */
export function isAustralianDst(atMs, standardOffsetMinutes) {
    const local = new Date(atMs + standardOffsetMinutes * 60_000);
    const year = local.getUTCFullYear();
    const month = local.getUTCMonth();
    const day = local.getUTCDate();
    if (month > 9 || month < 3)
        return true; // Nov, Dec, Jan, Feb
    if (month === 9)
        return day >= firstSundayOfMonth(year, 9); // October
    if (month === 3)
        return day < firstSundayOfMonth(year, 3); // April
    return false; // May–September
}
/**
 * Local offset in minutes east of UTC at `atMs` for an Australian state.
 * Falls back to AEST when the state is unknown — an hour of DST error beats
 * the ten hours of a UTC-as-local mistake.
 */
export function australianOffsetMinutes(state, atMs) {
    if (state === undefined)
        return DEFAULT_AU_OFFSET_MINUTES;
    const standard = STANDARD_OFFSET_MINUTES[state];
    if (!OBSERVES_DST.has(state))
        return standard;
    return isAustralianDst(atMs, standard) ? standard + 60 : standard;
}
/** Uppercase state tokens, longest first so ACT is not eaten by a stray "AC". */
const STATE_TOKEN = /\b(NSW|VIC|QLD|TAS|ACT|NT|SA|WA)\b/;
/**
 * Pull the state out of a single-line address ("29 Greenway CCT, Springfield
 * QLD 4300"), falling back to the trailing postcode. Returns undefined when
 * neither is readable — callers then get the AEST default.
 */
export function stateFromAddressLine(address) {
    if (!address)
        return undefined;
    const token = STATE_TOKEN.exec(address.toUpperCase());
    if (token?.[1])
        return token[1];
    const postcode = /\b(\d{4})\b(?!.*\b\d{4}\b)/.exec(address);
    return stateFromPostcode(postcode?.[1]);
}
/**
 * The offset to use for a report's derived times: the device's own value when
 * the field tool recorded one (it knows best — it was there), otherwise the
 * site's timezone, otherwise AEST.
 */
export function resolveInspectionOffsetMinutes(input) {
    if (input.deviceUtcOffsetMinutes !== undefined)
        return input.deviceUtcOffsetMinutes;
    const state = input.state ?? stateFromAddressLine(input.siteAddress);
    return australianOffsetMinutes(state, input.atMs);
}
//# sourceMappingURL=au-timezone.js.map